import React, { useState } from 'react';
import { Phone, MessageCircle, Calculator, ChevronRight, ChevronLeft, RotateCcw, AlertCircle, CheckCircle, Info } from 'lucide-react';

const CompensationEstimator = ({ isFullPage = false }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [answers, setAnswers] = useState({
    caseType: '',
    surgeryRequired: '',
    medicalBills: '',
    lostWages: '',
    faultClarity: '',
    state: ''
  });

  const questions = [
    {
      id: 'caseType',
      question: 'What type of case is this?',
      options: [
        { value: 'car', label: 'Car Accident' },
        { value: 'motorcycle', label: 'Motorcycle Crash' },
        { value: 'slipfall', label: 'Slip and Fall' },
        { value: 'maritime', label: 'Maritime / Jones Act Injury (Alaska or Washington)' },
        { value: 'firstresponder', label: 'First Responder / Peace Officer Crash' },
        { value: 'other', label: 'Other Personal Injury' }
      ]
    },
    {
      id: 'surgeryRequired',
      question: 'Was surgery or hospitalization required?',
      options: [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' }
      ]
    },
    {
      id: 'medicalBills',
      question: 'Approximate total medical bills so far?',
      options: [
        { value: 'low', label: 'Less than $10,000' },
        { value: 'moderate', label: '$10,000 – $50,000' },
        { value: 'high', label: '$50,000 – $150,000' },
        { value: 'veryhigh', label: 'More than $150,000' }
      ]
    },
    {
      id: 'lostWages',
      question: 'Have you missed work or lost wages?',
      options: [
        { value: 'none', label: 'No lost wages' },
        { value: 'weeks', label: 'A few weeks' },
        { value: 'months', label: 'Several months' },
        { value: 'year', label: 'A year or more' }
      ]
    },
    {
      id: 'faultClarity',
      question: 'Was fault clearly on the other side?',
      options: [
        { value: 'clear', label: 'Yes – clearly their fault' },
        { value: 'disputed', label: 'Somewhat disputed' },
        { value: 'highly_disputed', label: 'Highly disputed' }
      ]
    },
    {
      id: 'state',
      question: 'Which state did the injury occur in?',
      options: [
        { value: 'washington', label: 'Washington' },
        { value: 'alaska', label: 'Alaska' }
      ]
    }
  ];

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const nextStep = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const resetEstimator = () => {
    setCurrentStep(0);
    setShowResults(false);
    setAnswers({
      caseType: '',
      surgeryRequired: '',
      medicalBills: '',
      lostWages: '',
      faultClarity: '',
      state: ''
    });
  };

  const calculateEstimate = () => {
    let minRange = 15000;
    let maxRange = 75000;
    let severity = 'minor';

    // Medical bills factor
    if (answers.medicalBills === 'veryhigh') {
      minRange = 250000;
      maxRange = 1000000;
      severity = 'serious';
    } else if (answers.medicalBills === 'high') {
      minRange = 150000;
      maxRange = 500000;
      severity = 'serious';
    } else if (answers.medicalBills === 'moderate') {
      minRange = 75000;
      maxRange = 250000;
      severity = 'moderate';
    }

    // Surgery factor
    if (answers.surgeryRequired === 'yes') {
      minRange = Math.max(minRange, 75000);
      maxRange = Math.max(maxRange, 250000);
      if (severity === 'minor') severity = 'moderate';
    }

    // Lost wages factor
    if (answers.lostWages === 'year') {
      minRange *= 1.5;
      maxRange *= 1.5;
      severity = 'serious';
    } else if (answers.lostWages === 'months') {
      minRange *= 1.25;
      maxRange *= 1.25;
      if (severity === 'minor') severity = 'moderate';
    }

    // Maritime cases often have higher values
    if (answers.caseType === 'maritime') {
      minRange *= 1.2;
      maxRange *= 1.3;
    }

    // Fault clarity affects range
    if (answers.faultClarity === 'highly_disputed') {
      maxRange *= 0.7;
    } else if (answers.faultClarity === 'disputed') {
      maxRange *= 0.85;
    }

    // Round to nice numbers
    minRange = Math.round(minRange / 5000) * 5000;
    maxRange = Math.round(maxRange / 10000) * 10000;

    // Cap the maximum
    if (maxRange > 1000000) maxRange = 1000000;

    return { minRange, maxRange, severity };
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000) {
      return '$1,000,000+';
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'serious':
        return 'Serious Case';
      case 'moderate':
        return 'Moderate Case';
      default:
        return 'Minor to Moderate Case';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'serious':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'moderate':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      default:
        return 'bg-teal-100 text-teal-700 border-teal-200';
    }
  };

  const progress = ((currentStep + 1) / questions.length) * 100;
  const currentQuestion = questions[currentStep];
  const canProceed = answers[currentQuestion?.id];

  // Results View
  if (showResults) {
    const estimate = calculateEstimate();
    
    return (
      <div className={`bg-white rounded-2xl shadow-xl overflow-hidden ${isFullPage ? '' : 'max-w-2xl mx-auto'}`} data-testid="estimator-results">
        {/* Results Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-6 md:p-8 text-white text-center">
          <Calculator className="w-12 h-12 mx-auto mb-4 opacity-90" />
          <h3 className="font-heading text-2xl md:text-3xl font-bold mb-2">Your Estimated Range</h3>
          <p className="text-teal-100">Based on the information you provided</p>
        </div>

        <div className="p-6 md:p-8">
          {/* Estimate Display */}
          <div className="text-center mb-8">
            <div className={`inline-block px-4 py-2 rounded-full text-sm font-medium mb-4 border ${getSeverityColor(estimate.severity)}`}>
              {getSeverityLabel(estimate.severity)}
            </div>
            <div className="text-4xl md:text-5xl font-heading font-bold text-secondary mb-2">
              {formatCurrency(estimate.minRange)} – {formatCurrency(estimate.maxRange)}
            </div>
            <p className="text-slate-500">Potential compensation range</p>
          </div>

          {/* Factors Explanation */}
          <div className="bg-slate-50 rounded-xl p-6 mb-6">
            <h4 className="font-semibold text-secondary mb-4 flex items-center gap-2">
              <Info className="w-5 h-5 text-teal-600" />
              Factors That Affect Your Case Value
            </h4>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span>Total medical expenses and future treatment needs</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span>Lost wages and impact on earning capacity</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span>Pain, suffering, and quality of life impact</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span>Insurance policy limits of at-fault parties</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <span>Strength of evidence and clarity of fault</span>
              </li>
            </ul>
          </div>

          {/* Disclaimers */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-amber-800 mb-2">Important Disclaimers</h4>
                <ul className="text-sm text-amber-700 space-y-2">
                  <li>This is a <strong>general educational estimate only</strong> — every case is unique.</li>
                  <li>Actual value depends on many factors including insurance limits, medical evidence, and negotiation.</li>
                  <li><strong>I do not guarantee any specific amount.</strong> This tool is for informational purposes only.</li>
                  <li>Only a detailed case review can provide an accurate assessment of your claim's potential value.</li>
                </ul>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-teal-50 rounded-xl p-6 text-center">
            <h4 className="font-heading text-xl font-semibold text-secondary mb-3">
              Get a Free, Personalized Case Evaluation
            </h4>
            <p className="text-slate-600 mb-6">
              I'll review your specific situation and give you an honest assessment of your case's potential value.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:425-675-1224"
                className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-teal-700 transition-all shadow-lg"
                data-testid="estimator-cta-call"
              >
                <Phone className="w-5 h-5" />
                Call (425) 675-1224
              </a>
              <a
                href="sms:425-675-1224"
                className="inline-flex items-center justify-center gap-2 border-2 border-teal-600 text-teal-600 px-8 py-4 rounded-full font-semibold hover:bg-teal-50 transition-all"
                data-testid="estimator-cta-text"
              >
                <MessageCircle className="w-5 h-5" />
                Text Me
              </a>
            </div>
          </div>

          {/* Reset Button */}
          <div className="text-center mt-6">
            <button
              onClick={resetEstimator}
              className="inline-flex items-center gap-2 text-slate-500 hover:text-teal-600 transition-colors"
              data-testid="estimator-reset"
            >
              <RotateCcw className="w-4 h-4" />
              Reset & Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Question Form View
  return (
    <div className={`bg-white rounded-2xl shadow-xl overflow-hidden ${isFullPage ? '' : 'max-w-2xl mx-auto'}`} data-testid="compensation-estimator">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 p-6 md:p-8 text-white">
        <div className="flex items-center gap-3 mb-4">
          <Calculator className="w-8 h-8" />
          <h3 className="font-heading text-xl md:text-2xl font-bold">Potential Compensation Estimator</h3>
        </div>
        <p className="text-teal-100 text-sm md:text-base">
          Answer a few questions to get a rough idea of what your case might be worth. This is for educational purposes only.
        </p>
      </div>

      {/* Progress Bar */}
      <div className="px-6 pt-6">
        <div className="flex justify-between text-sm text-slate-500 mb-2">
          <span>Question {currentStep + 1} of {questions.length}</span>
          <span>{Math.round(progress)}% complete</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div 
            className="h-full bg-teal-500 transition-all duration-300 ease-out rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="p-6 md:p-8">
        <h4 className="font-heading text-lg md:text-xl font-semibold text-secondary mb-6">
          {currentQuestion.question}
        </h4>

        <div className="space-y-3">
          {currentQuestion.options.map((option) => (
            <button
              key={option.value}
              onClick={() => handleAnswer(currentQuestion.id, option.value)}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                answers[currentQuestion.id] === option.value
                  ? 'border-teal-500 bg-teal-50 text-teal-700'
                  : 'border-slate-200 hover:border-teal-300 hover:bg-slate-50'
              }`}
              data-testid={`option-${option.value}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  answers[currentQuestion.id] === option.value
                    ? 'border-teal-500 bg-teal-500'
                    : 'border-slate-300'
                }`}>
                  {answers[currentQuestion.id] === option.value && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
                <span className="font-medium">{option.label}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <button
            onClick={prevStep}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
              currentStep === 0
                ? 'text-slate-300 cursor-not-allowed'
                : 'text-slate-600 hover:text-teal-600 hover:bg-slate-100'
            }`}
            disabled={currentStep === 0}
            data-testid="estimator-prev"
          >
            <ChevronLeft className="w-5 h-5" />
            Back
          </button>

          <button
            onClick={nextStep}
            disabled={!canProceed}
            className={`inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold transition-all ${
              canProceed
                ? 'bg-teal-600 text-white hover:bg-teal-700 shadow-lg'
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
            data-testid="estimator-next"
          >
            {currentStep === questions.length - 1 ? 'See My Estimate' : 'Next'}
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Mini Disclaimer */}
        <p className="text-xs text-slate-400 text-center mt-6">
          This tool provides educational estimates only. Actual case values vary significantly based on individual circumstances.
        </p>
      </div>
    </div>
  );
};

export default CompensationEstimator;
